import { Goal, ID } from '@griply/types';

export type GetSubGoalsOptions = {
  goals: Goal[];
  recursive?: boolean;
};

export const getSubGoals = (
  id: ID | undefined,
  { goals, recursive = false }: GetSubGoalsOptions,
): Goal[] => {
  if (!id) {
    return [];
  }

  // Create a map of parent IDs to child goals for faster lookups
  const parentToChildrenMap = new Map<ID, Goal[]>();

  for (const goal of goals) {
    if (goal.parentIds?.length) {
      const parentId = goal.parentIds[goal.parentIds.length - 1];
      if (!parentToChildrenMap.has(parentId)) {
        parentToChildrenMap.set(parentId, []);
      }
      parentToChildrenMap.get(parentId)!.push(goal);
    }
  }

  // Get direct subgoals using the map
  const directSubGoals = parentToChildrenMap.get(id) || [];

  if (!recursive) {
    return directSubGoals;
  }

  // For recursive lookup, use a more efficient approach
  const allSubGoals: Goal[] = [...directSubGoals];
  const processedIds = new Set<ID>(directSubGoals.map((goal) => goal.id));

  let currentLevel = directSubGoals;

  // Breadth-first traversal
  while (currentLevel.length) {
    const nextLevel: Goal[] = [];

    // loop over current level goals to find their children
    for (const goal of currentLevel) {
      const children = parentToChildrenMap.get(goal.id) || [];

      for (const child of children) {
        if (!processedIds.has(child.id)) {
          processedIds.add(child.id);
          allSubGoals.push(child);
          // add found goals to the next level for further exploration
          nextLevel.push(child);
        }
      }
    }

    currentLevel = nextLevel;
  }

  return allSubGoals;
};
